/**
 * Source / UTM lus depuis ProLead.sourcePath (même convention que le backend).
 */

export const SOURCE_OPTIONS = [
  { value: 'manuel', label: 'Manuel' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'facebook', label: 'Facebook' },
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'email', label: 'Email' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'qr', label: 'QR code' },
  { value: 'terrain', label: 'Prospection terrain' },
  { value: 'autre', label: 'Autre' },
];

const SOURCE_LABELS = Object.fromEntries(SOURCE_OPTIONS.map((o) => [o.value, o.label]));
SOURCE_LABELS.manual = 'Manuel';
SOURCE_LABELS.direct = 'Site direct';

export function parseProLeadSource(sourcePath) {
  const raw = String(sourcePath || '').trim();
  if (!raw) {
    return { sourceKey: 'direct', label: 'Site direct', path: '/professionnels' };
  }
  let pathname = raw;
  let search = '';
  const qIndex = raw.indexOf('?');
  if (qIndex >= 0) {
    pathname = raw.slice(0, qIndex) || '/professionnels';
    search = raw.slice(qIndex + 1);
  }
  const params = new URLSearchParams(search);
  const utmSource = String(params.get('utm_source') || '').trim().toLowerCase();
  const isManual = /^\/?manual$/i.test(pathname) || pathname === 'manual';
  let sourceKey = utmSource;
  if (!sourceKey && isManual) sourceKey = 'manuel';
  if (!sourceKey) sourceKey = 'direct';
  const label = SOURCE_LABELS[sourceKey]
    || (utmSource ? utmSource.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase()) : 'Site direct');
  return {
    sourceKey,
    label,
    path: isManual ? 'manual' : pathname,
    utmSource,
  };
}

export function buildManualSourcePath(utmSource = 'manuel') {
  const key = String(utmSource || 'manuel').trim().toLowerCase() || 'manuel';
  return `manual?utm_source=${encodeURIComponent(key)}`;
}

/**
 * KPI DEFINITIONS =
 * - Nouveaux           = status === NEW
 * - À relancer         = nextFollowUpAt <= now && status ∉ {WON, LOST}
 * - Relances en retard = nextFollowUpAt < début du jour local && status ∉ {WON, LOST}
 * - Contactés          = status === CONTACTED
 * - Qualifiés          = status === QUALIFIED
 * - Gagnés             = status === WON
 * - Perdus             = status === LOST
 * - Taux qualification = (QUALIFIED + WON) / total
 * - Taux conversion    = WON / total (tous prospects)
 * - Par source         = agrégats Leads / Qualifiés+ / Gagnés / conversion
 */
export function computeProspectionKpis(leads = [], now = new Date()) {
  const list = Array.isArray(leads) ? leads : [];
  const total = list.length;
  const count = (status) => list.filter((l) => l.status === status).length;
  const nouveaux = count('NEW');
  const contactes = count('CONTACTED');
  const qualifies = count('QUALIFIED');
  const gagnes = count('WON');
  const perdus = count('LOST');

  const startOfDay = new Date(now);
  startOfDay.setHours(0, 0, 0, 0);

  const openStatuses = new Set(['NEW', 'CONTACTED', 'QUALIFIED']);
  const aRelancer = list.filter((l) => {
    if (!l.nextFollowUpAt || !openStatuses.has(l.status)) return false;
    return new Date(l.nextFollowUpAt).getTime() <= now.getTime();
  }).length;

  const enRetard = list.filter((l) => {
    if (!l.nextFollowUpAt || !openStatuses.has(l.status)) return false;
    return new Date(l.nextFollowUpAt).getTime() < startOfDay.getTime();
  }).length;

  const tauxQualification = total ? (qualifies + gagnes) / total : 0;
  const tauxConversion = total ? gagnes / total : 0;

  const bySourceMap = new Map();
  for (const lead of list) {
    const { label } = parseProLeadSource(lead.sourcePath);
    const row = bySourceMap.get(label) || { source: label, leads: 0, qualifies: 0, gagnes: 0 };
    row.leads += 1;
    if (lead.status === 'QUALIFIED' || lead.status === 'WON') row.qualifies += 1;
    if (lead.status === 'WON') row.gagnes += 1;
    bySourceMap.set(label, row);
  }
  const bySource = [...bySourceMap.values()]
    .map((row) => ({
      ...row,
      conversion: row.leads ? row.gagnes / row.leads : 0,
    }))
    .sort((a, b) => b.leads - a.leads);

  return {
    total,
    nouveaux,
    contactes,
    qualifies,
    gagnes,
    perdus,
    aRelancer,
    enRetard,
    tauxQualification,
    tauxConversion,
    bySource,
  };
}

export function formatPercent(ratio) {
  if (!Number.isFinite(ratio) || ratio <= 0) return '0 %';
  return `${(ratio * 100).toLocaleString('fr-FR', {
    maximumFractionDigits: 1,
    minimumFractionDigits: 0,
  })} %`;
}
